import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Auto-save hook for EMR forms
 * Automatically saves form data to localStorage and provides recovery
 */
export const useAutoSave = (formKey, initialData = {}, saveDelay = 2000) => {
  const [formData, setFormData] = useState(initialData);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasDraft, setHasDraft] = useState(false);
  const timeoutRef = useRef(null);
  const storageKey = `emr_draft_${formKey}`;

  // Check for existing draft on mount
  useEffect(() => {
    try {
      const savedDraft = localStorage.getItem(storageKey);
      if (savedDraft) {
        const parsed = JSON.parse(savedDraft);
        setHasDraft(true);
        // Don't auto-load, let user decide
      }
    } catch (error) {
      console.error('Error loading draft:', error);
    }
  }, [storageKey]);

  // Auto-save effect
  useEffect(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout for auto-save
    timeoutRef.current = setTimeout(() => {
      saveToLocalStorage();
    }, saveDelay);

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [formData]);

  const saveToLocalStorage = useCallback(() => {
    try {
      setIsSaving(true);
      const dataToSave = {
        data: formData,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(storageKey, JSON.stringify(dataToSave));
      setLastSaved(new Date());
      setHasDraft(true);
      setTimeout(() => setIsSaving(false), 500);
    } catch (error) {
      console.error('Error saving draft:', error);
      setIsSaving(false);
    }
  }, [formData, storageKey]);

  const loadDraft = useCallback(() => {
    try {
      const savedDraft = localStorage.getItem(storageKey);
      if (savedDraft) {
        const parsed = JSON.parse(savedDraft);
        setFormData(parsed.data);
        setLastSaved(new Date(parsed.timestamp));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error loading draft:', error);
      return false;
    }
  }, [storageKey]);

  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
      setHasDraft(false);
      setLastSaved(null);
    } catch (error) {
      console.error('Error clearing draft:', error);
    }
  }, [storageKey]);

  const updateField = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const updateFormData = useCallback((newData) => {
    setFormData(newData);
  }, []);

  const resetForm = useCallback(() => {
    setFormData(initialData);
    clearDraft();
  }, [initialData, clearDraft]);

  return {
    formData,
    setFormData: updateFormData,
    updateField,
    isSaving,
    lastSaved,
    hasDraft,
    loadDraft,
    clearDraft,
    resetForm,
    saveNow: saveToLocalStorage
  };
};

export default useAutoSave;